import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import type { AppDialogRequest } from "@/shared/utils/appDialog";
import "./styles/AppDialog.scss";

export interface AppDialogProps {
  request: AppDialogRequest | null;
  onDismiss: () => void;
}

export const AppDialog: React.FC<AppDialogProps> = ({ request, onDismiss }) => {
  useEffect(() => {
    if (!request) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (request.kind === "confirm") {
          request.resolve(false);
        } else {
          request.resolve();
        }
        onDismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [request, onDismiss]);

  if (!request || typeof document === "undefined") return null;

  const title =
    request.title ??
    (request.kind === "confirm" ? "Подтверждение" : "Сообщение");

  const closeWith = (ok: boolean) => {
    if (request.kind === "confirm") {
      request.resolve(ok);
    } else {
      request.resolve();
    }
    onDismiss();
  };

  return createPortal(
    <div
      className="app-dialog-overlay"
      role="presentation"
      onClick={() => closeWith(false)}
    >
      <div
        className="app-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        aria-describedby="app-dialog-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="app-dialog__title" id="app-dialog-title">
          {title}
        </h2>
        <p className="app-dialog__text" id="app-dialog-desc">
          {request.message}
        </p>
        <div className="app-dialog__actions">
          {request.kind === "confirm" ? (
            <>
              <button
                type="button"
                className="app-dialog__btn app-dialog__btn--secondary"
                onClick={() => closeWith(false)}
              >
                {request.cancelLabel ?? "Отмена"}
              </button>
              <button
                type="button"
                className={`app-dialog__btn ${
                  request.destructive
                    ? "app-dialog__btn--danger"
                    : "app-dialog__btn--primary"
                }`}
                onClick={() => closeWith(true)}
              >
                {request.confirmLabel ?? "ОК"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="app-dialog__btn app-dialog__btn--primary"
              onClick={() => closeWith(true)}
            >
              {request.okLabel ?? "ОК"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
